import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { PricingCategories, PricingCategoryFilters } from '../models';
import * as pricingCategoriesService from '../services/pricing-categories.service';

export const usePricingCategories = (
  defaultFilters: Partial<PricingCategoryFilters & { searchParams: { ids: string[] } }> = {},
) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<PricingCategoryFilters>>(otherDefaultFilters);

  const cacheData = pricingCategoriesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<PricingCategoryFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(pricingCategoriesService.pricingCategoriesQueryOptions(filters));

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    pricingCategoriesService.pricingCategoriesQueryOptions(
      { search: { codeId: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: PricingCategories['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
